import { defaultCurrency } from '@zapier/currency';

import {
  useAutoUpgrade,
  useCurrencies,
  useCurrentAccount,
} from '@zapier/upsells/src/hooks';

import {
  Currency,
  MemberRole,
  TrialType,
  UpgradeReason,
} from '@zapier/upsells/src/types';

import { useExtendTrial, usePaymentSource, usePlanForUpgradeReason } from '.';

type Props = {
  shouldAutoUpgrade: boolean;
  upgradeReason: UpgradeReason;
};

export const useUpgradeModal = ({
  shouldAutoUpgrade,
  upgradeReason,
}: Props) => {
  const {
    currencies,
    error: currenciesError,
    isLoading: isLoadingCurrencies,
  } = useCurrencies();

  const {
    currentAccount,
    error: accountError,
    isLoading: isLoadingAccount,
  } = useCurrentAccount();

  const {
    canExtendTrial,
    error: extendTrialError,
    extendTrial,
    isLoading: isLoadingExtendTrial,
  } = useExtendTrial({
    account: currentAccount,
    reason: upgradeReason,
    shouldAutoUpgrade,
  });

  const {
    autoUpgradeToPlan,
    error: autoUpgradeError,
    isAutoUpgradeEnabled,
    isLoading: isLoadingAutoUpgrade,
  } = useAutoUpgrade();

  const {
    error: paymentSourceError,
    isLoading: isLoadingPaymentSource,
    isPaymentMethodProvided,
  } = usePaymentSource({ accountId: currentAccount?.id });

  const activeTrialType = currentAccount?.isTrialingCompanies
    ? ('Company' as TrialType)
    : currentAccount?.isTrialingTeams
    ? ('Team' as TrialType)
    : currentAccount?.isTrialing
    ? ('Professional' as TrialType)
    : undefined;

  const {
    error: planForUpgradeReasonError,
    isLoading: isLoadingPlanForUpgradeReason,
    plan: destinationPlan,
  } = usePlanForUpgradeReason({
    account: currentAccount,
    activeTrialType,
    upgradeReason,
  });

  return {
    account: currentAccount,
    accountCurrency:
      currencies?.find((c: Currency) => c.name === currentAccount?.currency) ??
      defaultCurrency,
    activeTrialType,
    autoUpgradeToPlan,
    billingPeriodEndDate: currentAccount?.periodEndDate,
    billingPeriodStartDate: currentAccount?.periodStartDate,
    canExtendTrial,
    currentUserRole:
      currentAccount?.currentUserRole ?? ('member' as MemberRole),
    destinationPlan,
    enabledTrialExtensions: currentAccount?.enabledTrialExtensions ?? [],
    error:
      accountError ??
      autoUpgradeError ??
      currenciesError ??
      extendTrialError?.message ??
      paymentSourceError ??
      planForUpgradeReasonError,
    extendTrial,
    isAutoUpgradeEnabled,
    isLoading:
      isLoadingAccount ||
      isLoadingAutoUpgrade ||
      isLoadingCurrencies ||
      isLoadingExtendTrial ||
      isLoadingPaymentSource ||
      isLoadingPlanForUpgradeReason,
    isPaymentMethodProvided,
    taskBufferOverride: currentAccount?.taskBufferOverride ?? 1,
    tasksRemaining: currentAccount
      ? currentAccount?.usage.tasks.limit - currentAccount?.usage.tasks.total
      : 0,
  };
};
